import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAtom } from 'jotai';
import { getMatchedModalOpenAtom } from '~/state/ui-state-get-matched-modal';

const MODAL_PARAM = 'modal';
const MODAL_VALUE = 'get-matched';

const urlHasModalParam = () =>
    new URLSearchParams(window.location.search).get(MODAL_PARAM) ===
    MODAL_VALUE;

/**
 * Mirrors the Get Matched brief into the URL as `?modal=get-matched`.
 *
 * The questionnaire takes the whole screen, so it reads as a page — and the
 * first thing anyone does to leave a page they didn't mean to open is press
 * Back. Held only in the atom, Back navigated away from the listing under it
 * instead. Opening now pushes a history entry carrying the param, so Back
 * closes the brief and lands where the reader was; arriving with the param in
 * the URL (a shared link, or Forward) opens it.
 *
 * Raw history calls for the entries the hook pushes itself: the param means
 * nothing to any loader, and a router navigation would revalidate the whole
 * directory behind the modal on every open and close. The router never learns
 * about the pushed entry; popping back to the entry it already knows is a
 * no-op for it, and the popstate listener here is what closes the brief. The
 * one exception is closing a brief whose param the ROUTER knows (a deep link)
 * — that strip must go through the router, or its own location keeps the
 * param and re-emits it into the next URL it builds (the pill-bar search
 * carries every current param), resurrecting a brief the reader dismissed.
 */
export const useGetMatchedModalUrl = (): void => {
    const [open, setOpen] = useAtom(getMatchedModalOpenAtom);
    const navigate = useNavigate();
    /** Whether the entry currently on top is one this hook pushed. */
    const pushedRef = useRef(false);
    const openRef = useRef(open);
    openRef.current = open;
    const firstRunRef = useRef(true);

    // Keep the URL in step with the atom — except on the very first run,
    // where a param the page arrived with is an instruction to open, not a
    // leftover to clean off.
    useEffect(() => {
        if (firstRunRef.current) {
            firstRunRef.current = false;
            if (!open && urlHasModalParam()) {
                setOpen(true);
                return;
            }
        }
        if (open && !urlHasModalParam()) {
            const url = new URL(window.location.href);
            url.searchParams.set(MODAL_PARAM, MODAL_VALUE);
            window.history.pushState(window.history.state, '', url);
            pushedRef.current = true;
            return;
        }
        if (!open && urlHasModalParam()) {
            if (pushedRef.current) {
                // Closed in the UI: retire the entry the open pushed, so Back
                // doesn't step through a dead modal state.
                pushedRef.current = false;
                window.history.back();
                return;
            }
            // A deep-linked open closed in the UI: there is no entry of ours
            // to retire, so take the param off the one we're on — through the
            // router, which knows this URL. A raw replaceState here left the
            // router's own location carrying the param, and every later
            // navigation built from it (a search commit, a facet toggle) put
            // the param straight back.
            const params = new URLSearchParams(window.location.search);
            params.delete(MODAL_PARAM);
            const query = params.toString();
            void navigate(
                `${window.location.pathname}${query ? `?${query}` : ''}`,
                { replace: true, preventScrollReset: true },
            );
        }
    }, [open, setOpen, navigate]);

    // History moves while the brief and the URL disagree: Back off the entry
    // that carried the param closes the brief; Forward (or Back) onto one
    // that carries it opens it again.
    useEffect(() => {
        const onPopState = () => {
            if (openRef.current && !urlHasModalParam()) {
                pushedRef.current = false;
                setOpen(false);
                return;
            }
            if (!openRef.current && urlHasModalParam()) {
                // Not marked as ours: this entry may equally be a returned
                // deep link, and retiring a deep-link entry with
                // history.back() would leave the page — the router-driven
                // close above is the safe close for both.
                pushedRef.current = false;
                setOpen(true);
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [setOpen]);
};
